import React from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { getIsUnverified } from '../../publicSession/selectors';
import { ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import { ArtBrandedMobilePopup } from '../../ArtBrandedPopup';
import Button from '../../Button';
import { Props } from '../VerificationPopup';
import VerificationEmailInput from '../../VerificationEmailInput';
import s from './VerificationPopupMobile.scss';

const VerificationPopupMobile: React.FC<
  Omit<Props, 'dropFirstTimeFlag' | 'dropFlagOnClose'>
> = ({ actionType, email, resendEmail, onClose }) => {
  const { t } = useTranslation();

  let modalType;
  if (actionType === 'error') {
    modalType = 'error';
  } else if (actionType) {
    modalType = 'action';
  } else {
    modalType = 'welcome';
  }

  const isUnverified = useSelector(getIsUnverified);
  const title = t(`verification.title.${modalType}`, {
    action: t(`verification.action.${actionType}`),
  });

  return (
    <ArtBrandedMobilePopup
      artImageSrc={Url.staticCDNLink('eclipse/sisu/image-11-620625097.jpg')}
      artAuthorUsername={'AxiomDesign'}
      onClose={onClose}
      popupProps={{ isOpen: true, forceTheme: ThemeOptions.DARK }}
    >
      <div className={s['content']}>
        <div className={s['title']}>{title}</div>
        <div className={s['text']}>
          {t(`verification.checkEmail.${modalType}`, {
            postProcess: ['reactPostprocessor', 'featureBrancher'],
            email: <span className={s['email']}>{email}</span>,
          })}
        </div>
        <div className={s['text']}>
          {modalType === 'error' ? (
            <Trans i18nKey="verification.instructions.error">
              If the issue persists, try opening the verification link from a different device, and if that doesn&apos;t resolve the issue, please
              <a className="text-link-level-1-brand" href={URLS.SUPPORT}>contact DeviantArt Customer Support</a>.
            </Trans>
          ) : t(`verification.instructions.${modalType}`)}
        </div>
        <Button
          className={s['resend-button']}
          view="b3"
          fullWidth={false}
          onClick={() => {
            resendEmail();
            onClose && onClose();
          }}
        >
          {t('verification.resend')}
        </Button>
      </div>
      <div className={s['footer']}>
        {isUnverified ? (
          <div className={s['footer-blurb']}>
            <Trans i18nKey="verification.doormatBlurb">
              If you don&apos;t recognize this email or no longer have access to
              it, please contact support at
              <a
                target="_blank"
                rel="noreferrer"
                href="mailto:accountrecovery@deviantart.com"
              >
                accountrecovery@deviantart.com
              </a>{' '}
              or
              <a
                target="_blank"
                rel="noreferrer"
                href="https://www.deviantartsupport.com/en/article/what-should-i-do-if-ive-lost-access-to-my-account"
              >
                learn more here
              </a>
              .
            </Trans>
          </div>
        ) : (
          <div className={s['change-email']}>
            {t('verification.notEmail', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              email: <span className={s['email']}>{email}</span>,
            })}
            <VerificationEmailInput className={s['change-email-input']} />
          </div>
        )}
      </div>
    </ArtBrandedMobilePopup>
  );
};

export default VerificationPopupMobile;
